/**
 * Global toast (notification) store.
 *
 * A single publish/subscribe store that powers ONE notification layer rendered
 * at the application root (see GlobalToastLayer in shared/ToastLayer.tsx).
 * Every role - Applicant, Admin, Referrer, Employer, Sponsor - pushes through
 * this store so notifications are always shown identically, above every popup,
 * sidebar, drawer, table and sticky header, regardless of which page is open.
 */

export type GlobalToastType = 'success' | 'error' | 'warning' | 'info';

export interface GlobalToast {
  id: string;
  type: GlobalToastType;
  title?: string;
  message: string;
}

const AUTO_DISMISS_MS = 5000;

let toasts: GlobalToast[] = [];
const listeners = new Set<(toasts: GlobalToast[]) => void>();
const timers = new Map<string, ReturnType<typeof setTimeout>>();

function emit() {
  const snapshot = toasts;
  listeners.forEach((fn) => fn(snapshot));
}

/** Show a global toast. Returns the toast id (for early dismissal). */
export function notifyToast(
  type: GlobalToastType,
  message: string,
  title?: string,
): string {
  const id = `toast_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  toasts = [...toasts, { id, type, message, title }];
  emit();
  const timer = setTimeout(() => dismissToast(id), AUTO_DISMISS_MS);
  timers.set(id, timer);
  return id;
}

notifyToast.success = (message: string, title?: string) => notifyToast('success', message, title);
notifyToast.error = (message: string, title?: string) => notifyToast('error', message, title);
notifyToast.warning = (message: string, title?: string) => notifyToast('warning', message, title);
notifyToast.info = (message: string, title?: string) => notifyToast('info', message, title);

/** Dismiss a toast by id (also cancels its auto-dismiss timer). */
export function dismissToast(id: string): void {
  const timer = timers.get(id);
  if (timer) { clearTimeout(timer); timers.delete(id); }
  const next = toasts.filter((t) => t.id !== id);
  if (next.length !== toasts.length) {
    toasts = next;
    emit();
  }
}

/**
 * Clear every visible toast (and cancel all auto-dismiss timers).
 * Called on navigation: a toast raised on one screen (e.g. "Referral link
 * copied to clipboard") must never follow the user onto the next screen.
 */
export function clearAllToasts(): void {
  timers.forEach((t) => clearTimeout(t));
  timers.clear();
  if (toasts.length > 0) {
    toasts = [];
    emit();
  }
}

/** Subscribe to toast updates. Returns an unsubscribe function. */
export function subscribeToasts(
  fn: (toasts: GlobalToast[]) => void,
): () => void {
  listeners.add(fn);
  fn(toasts);
  return () => { listeners.delete(fn); };
}
